/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 단일컬럼의 단순화된 목록만 제공하는 그리드 클래스를 정의한다.
 *
 * @file /src/components/list/List.ts
 * @author sungjin <esung246@naddle.net>
 * @license MIT License
 * @modified 2026. 6. 24.
 */
import * as parent from '@models/table/Table';
import { ListStores, TreeStores } from '@models/store/Store';
import { Properties as Column } from '@models/table/Column';
import { Record } from '@models/store/Record';
import './List.css';

export interface Listeners<T> extends parent.Listeners<T> {}

export interface Properties<S extends ListStores | TreeStores, T extends parent.Table> extends parent.Properties<S, T> {
    /**
     * 단일컬럼의 dataIndex 또는 Renderer 함수
     */
    dataIndex: string | Column<T>['renderer'];

    /**
     * 다중선택을 허용할지 여부, 기본적으로 목록은 선택사용이 기본값
     */
    multiple?: boolean;

    /**
     * 트리에서 부모 항목 선택 시 하위 항목을 함께 선택할지 여부 (기본값 true)
     */
    cascade?: boolean;

    /**
     * 트리스토어 사용 시 자식 레코드가 존재할 경우 자동으로 확장할지 여부
     */
    autoExpand?: boolean;

    /**
     * 트리스토어 사용 시 확장할 트리뎁스
     */
    expandedDepth?: number;

    /**
     * 항목의 선택가능여부
     */
    selectable?: boolean | ((record: Record) => boolean);

    /**
     * 이벤트 리스너
     */
    listeners?: Listeners<T>;
}

export class List<S extends ListStores | TreeStores = ListStores | TreeStores> extends parent.Table<S, List<S>> {
    /**
     * 컴포넌트 확장타입
     */
    variant = 'list';

    /**
     * 자식 레코드가 존재할 경우 자동으로 확장할지 여부
     */
    autoExpand: boolean = false;

    /**
     * 확장할 트리뎁스
     */
    expandedDepth: number | null = null;

    /**
     * 목록 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(
        properties: Omit<
            Properties<S, List<S>>,
            'columns' | 'selection' | 'freeze' | 'focusableRow' | 'focusableCell' | 'summary' | 'isLazyRender'
        > = null
    ) {
        super({
            ...properties,
            columns: [
                {
                    text: null,
                    dataIndex: typeof properties.dataIndex == 'string' ? properties.dataIndex : null,
                    renderer: typeof properties.dataIndex == 'function' ? properties.dataIndex : null,
                    flex: 1,
                },
            ],
            selection: {
                selectable: properties.selectable,
                multiple: properties.multiple === true,
                additive: properties.multiple === true,
                keepable: true,
                deselectable: properties.multiple === true,
                cascade: properties.cascade !== false,
            },
            focusableRow: true,
            focusableCell: false,
            summary: false,
            isLazyRender: false,
            scrollable: 'y',
        } as parent.Properties<S, List<S>>);

        this.autoExpand = properties.autoExpand === true;
        this.expandedDepth = properties.expandedDepth ?? null;
    }

    /**
     * 데이터 형태가 트리인지 확인한다.
     *
     * @returns 트리여부
     */
    isTree(): boolean {
        return this.store.type == 'tree';
    }

    /**
     * 전체트리를 확장한다.
     *
     * @param expandedDepth - 확장할 트리뎁스
     */
    async expandAll(expandedDepth: number = null): Promise<void> {
        if (this.isTree() == false) {
            return;
        }

        await this.body.expandAll(expandedDepth);
    }

    /**
     * 데이터스토어가 로딩되었을 때
     */
    async onLoad(): Promise<void> {
        await super.onLoad();

        if (this.isTree() == true) {
            if (this.autoExpand == true) {
                await this.body.autoExpand();
            }

            if (this.expandedDepth !== null) {
                await this.expandAll(this.expandedDepth);
            }
        }
    }
}
